import type {
  MealCompletePickupData,
  MealCompletePickupInternalData,
  MealMonitoringDetailData,
  MealMonitoringDetailItem,
  MealMonitoringDetailStatus,
  MealMonitoringDetailSummary,
} from "../../../../api/meal/types/mealMonitoringDetailType";

interface ApplyLocalMealCompletionArgs {
  detail: MealMonitoringDetailData;
  sourceTabTitle: string;
  item: MealMonitoringDetailItem;
  pickupData: MealCompletePickupData;
  statusFilter?: MealMonitoringDetailStatus[];
}

export interface ApplyLocalMealCompletionResult {
  detail: MealMonitoringDetailData;
  completedItem: MealMonitoringDetailItem;
  targetTabTitle: string;
}

const normalize = (value: string | null | undefined): string => {
  return (value ?? "").trim().toLowerCase();
};

const isInternalPickupData = (
  pickupData: MealCompletePickupData,
): pickupData is MealCompletePickupInternalData => {
  return "menu" in pickupData;
};

const formatDetailDate = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const pad = (number: number) => String(number).padStart(2, "0");

  return `${date.getFullYear()}-${pad(
    date.getMonth() + 1,
  )}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const findFinishedDescription = (detail: MealMonitoringDetailData): string => {
  return (
    detail.filter.status.find(
      (status) => normalize(status.status) === "finished",
    )?.description ?? "Sudah Diambil"
  );
};

const findMenuSummary = (detail: MealMonitoringDetailData, menuId: number) => {
  for (const tab of detail.meal.tab) {
    const menu = tab.summary?.menu.find((value) => value.menu_id === menuId);

    if (menu) {
      return menu;
    }
  }

  return null;
};

const findTabTitle = (
  detail: MealMonitoringDetailData,
  title: string,
): string | null => {
  return (
    detail.meal.tab.find((tab) => normalize(tab.title) === normalize(title))
      ?.title ?? null
  );
};

const isSameItem = (
  candidate: MealMonitoringDetailItem,
  source: MealMonitoringDetailItem,
): boolean => {
  if (source.uuid && candidate.uuid) {
    return source.uuid === candidate.uuid;
  }

  if (
    source.employee_id !== null &&
    source.employee_id !== undefined &&
    candidate.employee_id !== null &&
    candidate.employee_id !== undefined
  ) {
    return source.employee_id === candidate.employee_id;
  }

  return candidate.nrp === source.nrp && candidate.type === source.type;
};

const updateSummary = (
  summary: MealMonitoringDetailSummary | null,
  menuId: number | null,
  mode: "finish" | "remove" | "add-finished",
): MealMonitoringDetailSummary | null => {
  if (!summary) {
    return null;
  }

  const next: MealMonitoringDetailSummary = {
    ...summary,
    menu: summary.menu.map((menu) => ({
      ...menu,
    })),
  };

  if (mode === "finish") {
    next.taken += 1;
    next.ordered = Math.max(0, next.ordered - 1);
  }

  if (mode === "remove") {
    next.total = Math.max(0, next.total - 1);

    next.ordered = Math.max(0, next.ordered - 1);
  }

  if (mode === "add-finished") {
    next.total += 1;
    next.taken += 1;
  }

  if (menuId !== null) {
    next.menu = next.menu.map((menu) => {
      if (menu.menu_id !== menuId) {
        return menu;
      }

      if (mode === "finish") {
        return {
          ...menu,
          taken: menu.taken + 1,
          ordered: Math.max(0, menu.ordered - 1),
        };
      }

      if (mode === "remove") {
        return {
          ...menu,
          total: Math.max(0, menu.total - 1),
          ordered: Math.max(0, menu.ordered - 1),
        };
      }

      return {
        ...menu,
        total: menu.total + 1,
        taken: menu.taken + 1,
      };
    });
  }

  return next;
};

export const buildCompletedMealItem = (
  detail: MealMonitoringDetailData,
  item: MealMonitoringDetailItem,
  pickupData: MealCompletePickupData,
): MealMonitoringDetailItem => {
  const finishedDescription = findFinishedDescription(detail);

  if (isInternalPickupData(pickupData)) {
    const menuSummary = findMenuSummary(detail, pickupData.menu.id);

    return {
      ...item,

      id: pickupData.id,
      uuid: pickupData.uuid,

      employee_id: pickupData.employee_id,

      menu_id: pickupData.menu.id,

      menu_uuid: pickupData.menu.uuid,

      menu_name: pickupData.menu.menu_name,

      category_id:
        menuSummary?.category_id ?? pickupData.menu.meals_category_id,

      category: menuSummary?.category ?? item.category,

      type: pickupData.type,

      reason: pickupData.reason,

      status: pickupData.status,

      status_description: finishedDescription,

      quota_source: pickupData.quota_source,

      created_at: formatDetailDate(pickupData.created_at),

      updated_at: formatDetailDate(
        pickupData.updated_at ?? pickupData.created_at,
      ),
    };
  }

  return {
    ...item,

    id: pickupData.id,
    uuid: pickupData.uuid,

    external_type: pickupData.type ?? item.external_type,

    status: pickupData.status,

    status_description: finishedDescription,

    quota_source: item.quota_source ?? "external",

    created_at: item.created_at ?? formatDetailDate(pickupData.created_at),

    updated_at: formatDetailDate(
      pickupData.updated_at ?? pickupData.created_at,
    ),
  };
};

const resolveTargetTabTitle = (
  detail: MealMonitoringDetailData,
  sourceTabTitle: string,
  item: MealMonitoringDetailItem,
  pickupData: MealCompletePickupData,
): string => {
  if (!isInternalPickupData(pickupData)) {
    return sourceTabTitle;
  }

  const isUnallocated =
    normalize(sourceTabTitle) === "belum dialokasikan" || item.menu_id === null;

  if (!isUnallocated) {
    return sourceTabTitle;
  }

  const quotaSource = normalize(pickupData.quota_source);

  if (quotaSource === "order") {
    return findTabTitle(detail, "Pesanan") ?? sourceTabTitle;
  }

  if (quotaSource === "spare") {
    return findTabTitle(detail, "Spare") ?? sourceTabTitle;
  }

  return sourceTabTitle;
};

export const applyLocalMealCompletion = ({
  detail,
  sourceTabTitle,
  item,
  pickupData,
  statusFilter,
}: ApplyLocalMealCompletionArgs): ApplyLocalMealCompletionResult => {
  const completedItem = buildCompletedMealItem(detail, item, pickupData);

  const targetTabTitle = resolveTargetTabTitle(
    detail,
    sourceTabTitle,
    item,
    pickupData,
  );

  const sourceTitleNormalized = normalize(sourceTabTitle);

  const targetTitleNormalized = normalize(targetTabTitle);

  const hideFinishedItem =
    Array.isArray(statusFilter) &&
    statusFilter.length > 0 &&
    !statusFilter.some((value) => normalize(value) === "finished");

  const sourceEqualsTarget = sourceTitleNormalized === targetTitleNormalized;

  const sourceTab = detail.meal.tab.find(
    (tab) => normalize(tab.title) === sourceTitleNormalized,
  );

  const sourceHasItem = sourceTab?.items.some((value) =>
    isSameItem(value, item),
  );

  if (!sourceHasItem) {
    return {
      detail,
      completedItem,
      targetTabTitle,
    };
  }

  const nextTabs = detail.meal.tab.map((tab) => ({
    ...tab,

    items: [...tab.items],

    summary: tab.summary
      ? {
          ...tab.summary,
          menu: tab.summary.menu.map((menu) => ({
            ...menu,
          })),
        }
      : null,
  }));

  const sourceIndex = nextTabs.findIndex(
    (tab) => normalize(tab.title) === sourceTitleNormalized,
  );

  const targetIndex = nextTabs.findIndex(
    (tab) => normalize(tab.title) === targetTitleNormalized,
  );

  if (sourceIndex === -1) {
    return {
      detail,
      completedItem,
      targetTabTitle,
    };
  }

  if (sourceEqualsTarget) {
    nextTabs[sourceIndex].items = hideFinishedItem
      ? nextTabs[sourceIndex].items.filter(
          (candidate) => !isSameItem(candidate, item),
        )
      : nextTabs[sourceIndex].items.map((candidate) =>
          isSameItem(candidate, item) ? completedItem : candidate,
        );

    nextTabs[sourceIndex].summary = updateSummary(
      nextTabs[sourceIndex].summary,
      completedItem.menu_id,
      "finish",
    );
  } else {
    nextTabs[sourceIndex].items = nextTabs[sourceIndex].items.filter(
      (candidate) => !isSameItem(candidate, item),
    );

    nextTabs[sourceIndex].summary = updateSummary(
      nextTabs[sourceIndex].summary,
      item.menu_id,
      "remove",
    );

    if (targetIndex !== -1) {
      if (!hideFinishedItem) {
        const alreadyExists = nextTabs[targetIndex].items.some((candidate) =>
          isSameItem(candidate, completedItem),
        );

        if (!alreadyExists) {
          nextTabs[targetIndex].items.push(completedItem);
        }
      }

      nextTabs[targetIndex].summary = updateSummary(
        nextTabs[targetIndex].summary,
        completedItem.menu_id,
        "add-finished",
      );
    }
  }

  return {
    detail: {
      ...detail,
      meal: {
        ...detail.meal,
        tab: nextTabs,
      },
    },

    completedItem,
    targetTabTitle,
  };
};
